import { createElement } from 'react';
import ReactDOMServer from 'react-dom/server';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import LinkRenderer from '@/components/LinkRenderer';
import { convertFilesForUpload } from '@/features/msGraph/helper';
import { MSG_TYPE } from '@/utils/folders';

const EMAIL_MIME_TYPES = [MSG_TYPE, 'message/rfc822'];

export const escapeHtml = (value = '') =>
	String(value)
		.replaceAll('&', '&amp;')
		.replaceAll('<', '&lt;')
		.replaceAll('>', '&gt;')
		.replaceAll('"', '&quot;')
		.replaceAll(/'/g, '&#39;');

export const stripHtml = (value = '') =>
	String(value)
		.replace(/<style[\s\S]*?<\/style>/gi, ' ')
		.replace(/<script[\s\S]*?<\/script>/gi, ' ')
		.replace(/<[^>]+>/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();

export const truncate = (value, max = 1200) => {
	const text = String(value ?? '').trim();
	return text.length > max ? `${text.slice(0, max - 3)}...` : text;
};

export const toJsonText = (value, max = 4000) => truncate(JSON.stringify(value ?? []), max);

export const getSortDate = (item) => {
	const value =
		item?.sortDate ??
		item?.lastModifiedDateTime ??
		item?.createdDateTime ??
		item?.timestamp ??
		item?.date ??
		null;
	return value ? new Date(value).getTime() : 0;
};

export const isEmailSharepointFile = (file) => {
	const mimeType = String(file?.file?.mimeType ?? '').toLowerCase();
	const name = String(file?.name ?? '').toLowerCase();
	return EMAIL_MIME_TYPES.includes(mimeType) || name.endsWith('.msg') || name.endsWith('.eml');
};

export const buildPolicySummaryLine = (policy = {}) =>
	[
		policy.PolicyNumber ? `Policy ${policy.PolicyNumber}` : null,
		policy.Description,
		policy.TypeDescription,
		policy.ClassOfRisk,
		policy.InsurerName,
		policy.FromDate ? `From ${policy.FromDate}` : null,
		policy.ToDate ? `To ${policy.ToDate}` : null,
	]
		.filter(Boolean)
		.join(' | ');

export const buildPolicyTransactionSummaryLine = (transaction = {}) =>
	[
		transaction.PolicyNumber ? `Policy ${transaction.PolicyNumber}` : null,
		transaction.InvoiceNumber ? `Invoice ${transaction.InvoiceNumber}` : null,
		transaction.BriefDescription,
		transaction.ClassOfRiskName,
		transaction.TransactionTypeName,
		transaction.InsurerName,
		transaction.EffectiveDate ? `Effective ${transaction.EffectiveDate}` : null,
	]
		.filter(Boolean)
		.join(' | ');

// Renders with the same Markdown setup as the Ask AI drawer, so saved output matches what was shown
export const markdownToHtml = (markdown) =>
	ReactDOMServer.renderToStaticMarkup(
		createElement(
			ReactMarkdown,
			{ components: { a: LinkRenderer, link: LinkRenderer }, remarkPlugins: [remarkGfm] },
			markdown,
		),
	);

export const buildCitationHtml = (citations = []) => {
	if (!citations.length) return '';
	return `<hr/><p><strong>Sources</strong></p><ul>${citations
		.map(
			(citation) =>
				`<li>${escapeHtml(citation.title ?? 'Source')}${citation.snippet ? ` - ${escapeHtml(citation.snippet)}` : ''}</li>`,
		)
		.join('')}</ul>`;
};

export const extractLabeledValue = (content = '', label) => {
	const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	const match = String(content ?? '').match(
		new RegExp(
			`(?:^|\\n)\\s*${escapedLabel}\\s*:\\s*(.+?)(?=\\n\\s*[A-Za-z][A-Za-z ]*:\\s|$)`,
			'is',
		),
	);
	return match?.[1]?.trim() ?? '';
};

export const stripMarkdown = (value = '') =>
	String(value ?? '')
		.replace(/^#{1,6}\s+/gm, '')
		.replace(/\*\*(.*?)\*\*/g, '$1')
		.replace(/\*(.*?)\*/g, '$1')
		.replace(/`([^`]+)`/g, '$1')
		.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
		.replace(/^\s*[-*+]\s+/gm, '')
		.replace(/\n{3,}/g, '\n\n')
		.trim();

export const buildTaskPrefillFromMessage = (message, contextLabel) => {
	const content = String(message?.content ?? '');
	const suggestedTitle =
		extractLabeledValue(content, 'Task title') ||
		extractLabeledValue(content, 'Title') ||
		'Review and follow up';
	const why = extractLabeledValue(content, 'Why');
	const dueDate = extractLabeledValue(content, 'Recommended due date');
	const priority = extractLabeledValue(content, 'Priority');
	const details = extractLabeledValue(content, 'Task details');

	const bodySections = [
		why ? `Why:\n${stripMarkdown(why)}` : null,
		dueDate ? `Recommended due date:\n${stripMarkdown(dueDate)}` : null,
		priority ? `Priority:\n${stripMarkdown(priority)}` : null,
		details ? `Task details:\n${stripMarkdown(details)}` : null,
		`AI suggestion:\n${stripMarkdown(content)}`,
	].filter(Boolean);

	return {
		subject: stripMarkdown(suggestedTitle).slice(0, 120) || `Follow up for ${contextLabel}`,
		body: bodySections.join('\n\n'),
		priority: /HIGH|URGENT/i.test(priority) ? 'HIGH' : /LOW/i.test(priority) ? 'LOW' : 'NONE',
	};
};

export const buildUnlayerDesignFromHtml = (html) => ({
	counters: {
		u_row: 1,
		u_column: 1,
		u_content_text: 1,
	},
	body: {
		rows: [
			{
				id: 'row-1',
				cells: [1],
				columns: [
					{
						id: 'col-1',
						contents: [
							{
								id: 'text-1',
								type: 'text',
								values: {
									containerPadding: '10px',
									textAlign: 'left',
									color: '#172033',
									lineHeight: '160%',
									selectable: true,
									draggable: true,
									text: html,
								},
							},
						],
						values: {
							backgroundColor: '',
							padding: '0px',
							border: {},
							verticalAlign: 'top',
						},
					},
				],
				values: {
					displayCondition: null,
					columns: false,
					backgroundColor: '',
					columnsBackgroundColor: '',
					backgroundImage: {},
					padding: '0px',
				},
			},
		],
		values: {
			backgroundColor: '#FFFFFF',
			contentWidth: '600px',
			fontFamily: {
				label: 'Arial',
				value: 'Arial, Helvetica, sans-serif',
			},
		},
	},
});

export const readFileAsBase64 = (file) =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const result = String(reader.result ?? '');
			const [, base64 = ''] = result.split(',');
			resolve(base64);
		};
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});

export const normalizeAskAiMessageAttachment = (file = {}) => ({
	id: file.id ?? file.fileId ?? file.attachmentId ?? null,
	name: file.name ?? file.fileName ?? 'Attachment',
	webUrl: file.webUrl ?? file.url ?? file.listItem?.webUrl ?? null,
});

export const getMessageAttachments = (message) => {
	const raw = message?.attachments ?? [];
	if (!Array.isArray(raw)) return [];

	return raw.map(normalizeAskAiMessageAttachment).filter((attachment) => attachment.name);
};

export const uploadSharepointFilesToSession = async ({
	files = [],
	sessionId,
	uploadDocument,
	sharepointDriveId,
	sharepointItemId,
}) => {
	if (!sessionId || files.length === 0) {
		return { failedFiles: [], uploadedFiles: [] };
	}

	const failedFiles = [];
	const uploadedFiles = [];

	//Catch any upload errors and return files that failed to upload...
	for (const sourceFile of files) {
		const fileForConvert =
			sourceFile instanceof File
				? sourceFile
				: {
						...sourceFile,
						driveId:
							sourceFile.driveId ??
							sourceFile.parentReference?.driveId ??
							sharepointDriveId,
						downloadUrl:
							sourceFile['@microsoft.graph.downloadUrl'] ?? sourceFile.downloadUrl,
					};

		const converted = await convertFilesForUpload([fileForConvert]);
		const entry = converted[0];

		if (!entry?.file) {
			failedFiles.push(sourceFile);
			continue;
		}

		try {
			const base64 = await readFileAsBase64(entry.file);
			const attachment = await uploadDocument({
				sessionId,
				fileName: entry.file.name,
				contentType: entry.file.type,
				base64,
				sharepointDriveId: sharepointDriveId ?? null,
				sharepointItemId: sharepointItemId ?? null,
			}).unwrap();

			uploadedFiles.push(
				normalizeAskAiMessageAttachment({
					...(sourceFile instanceof File ? {} : sourceFile),
					...attachment,
					name: entry.file.name,
				}),
			);
		} catch {
			failedFiles.push(sourceFile);
		}
	}

	return { failedFiles, uploadedFiles };
};
